import format from "date-fns/format"
import { Form, Formik } from "formik"
import { useTranslation } from "react-i18next"
import { useUpdateArticleSettingsMutation } from "../../api/controllers/knowledgeBase/newsline"
import { formTranslation } from "../../locales/form"
import { NewslineFormValues, NewslineKeys, NewslineSettings } from "../../models/knowledgeBase/newsline"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { saveNotificationSuccess } from "../../store/notifications/thunks"
import { selectCurrentProject } from "../../store/projects/selectors"
import { testId } from "../../utility/tests/testId/knowledgeBase"
import CheckBoxValidatableInput from "../CheckBoxValidatableInput/CheckBoxValidatableInput"
import { DatePickerInput } from "../DatePickerInput"
import InfoIcon from "../InfoIcon/InfoIcon"
import LoadingButton from "../LoadingButton/LoadingButton"
import UserAccessForm from "../UserAccessForm/UserAccessForm"
import DebouncedValidatableInput from "../ValidatableInput/DebouncedValidatableInput"
import { getNewslineDefaultValues } from "./constants"
import styles from "./NewslineForm.module.scss"
import { NewslineFormSchema } from "./schemas/NewslineForm.schema"
import { selectCurrentArticle } from "../../store/knowledgeBase/selectors"
import { NEWSLINE_FORM_CONST } from "./NewslineForm.const"

const { tNamespace, tPaths } = NEWSLINE_FORM_CONST

export interface NewslineFormProps {
    newsline?: NewslineSettings
    close: () => void
}

export function NewslineForm({ newsline, close }: NewslineFormProps) {
    const dispatch = useAppDispatch()
    const { t } = useTranslation(tNamespace)
    const project = useAppSelector(selectCurrentProject)
    const article = useAppSelector(selectCurrentArticle)
    const newslineValue = newsline ? newsline : getNewslineDefaultValues(project?.id)
    const [updateArticleSettings, { isLoading }] = useUpdateArticleSettingsMutation()

    const handleSubmit = async (values: NewslineFormValues) => {
        if (project && article && article.Article) {
            const res = await updateArticleSettings({
                settings: values,
                projectId: project.id,
                articleId: article.Article.Id,
                symbolCode: article.Article.SymbolCode
            })
            if (res && "data" in res && res.data) {
                saveNotificationSuccess(dispatch, {
                    Title: {
                        Value: t(tPaths.form.success),
                        NeedLocalization: false
                    }
                })
                close()
            }
        }
    }

    return (
        <Formik<NewslineFormValues>
            initialValues={newslineValue}
            validationSchema={NewslineFormSchema}
            onSubmit={handleSubmit}
        >
            {formikProps => {
                const { handleSubmit, setFieldValue, values } = formikProps

                return (
                    <Form className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.form__section}>
                            <CheckBoxValidatableInput
                                id={NewslineKeys.isInNewsline}
                                name={NewslineKeys.isInNewsline}
                                label={t(tPaths.form.isInNewsline)}
                                className={styles.form__checkbox}
                            />
                            <CheckBoxValidatableInput
                                id={NewslineKeys.isHideAfterReading}
                                name={NewslineKeys.isHideAfterReading}
                                label={t(tPaths.form.isHideAfterReading)}
                                className={styles.form__checkbox}
                            />
                            <CheckBoxValidatableInput
                                id={NewslineKeys.isEndless}
                                name={NewslineKeys.isEndless}
                                label={t(tPaths.form.isEndless)}
                                className={styles.form__checkbox}
                            />
                        </div>
                        <div className={styles.form__section}>
                            <DatePickerInput
                                name={NewslineKeys.lifeTime}
                                label={t(tPaths.form.lifeTime)}
                                value={values.LifeTime > 0 ? format(values.LifeTime, "dd.MM.yyyy HH:mm") : ""}
                                onChange={(dates: Date[]) => {
                                    setFieldValue(NewslineKeys.lifeTime, dates[0].getTime(), true)
                                }}
                                onClear={() => {
                                    setFieldValue(NewslineKeys.lifeTime, 0)
                                }}
                                showTimeInput
                                minDateFromNow={60000}
                                disabled={values.IsEndless}
                            />
                        </div>
                        <UserAccessForm
                            type="newsline"
                            title={t(tPaths.form.subscriptions)}
                            name={NewslineKeys.subscriptions}
                            currentProject={project}
                            disableProjectsSelector
                            rolesPlaceholder={t(tPaths.form.subscriptionsPlaceholder)}
                            {...formikProps}
                        />
                        <div className={styles.form__section}>
                            <DebouncedValidatableInput
                                as="textarea"
                                id={NewslineKeys.description}
                                name={NewslineKeys.description}
                                label={t(tPaths.form.description)}
                                className={styles.form__textarea}
                                placeholder={t(formTranslation.enterText)}
                                icon={
                                    <InfoIcon
                                        id="tooltipNewslineDescription"
                                        placement="top"
                                        content={t(tPaths.form.descriptionTooltip)}
                                    />
                                }
                            />
                        </div>
                        <LoadingButton
                            loading={isLoading}
                            className={styles.form__btn}
                            type="submit"
                            data-testid={testId.saveNewsline}
                        >
                            {t(formTranslation.save)}
                        </LoadingButton>
                    </Form>
                )
            }}
        </Formik>
    )
}
